'use client';

import { getImageUrl } from '@/app/utils';
import { fetchBlogData } from '@/fetchSSR/blog/getBlogData';
import { baseURLImg } from '@/services/constants/app';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';

interface BlogData {
    blog_id: number;
    blog_title: string;
    blog_description: string;
    meta_description: string;
    blog_image: string;
    category: string;
    created_date: string;
    created_by: string;
}

interface BlogCommonProps {
    blogData: BlogData[];
}

export default function BlogCommon({ blogData }: BlogCommonProps) {
    // const getImageUrl = (imageName: string) =>
    //     imageName.startsWith('http') ? imageName : `${baseURLImg}Images/${imageName.replace(/^\/+/, '')}`;

    // Sort blogData in descending order by created_date
    const sortedBlogData = [...blogData].sort((a, b) => new Date(b.created_date).getTime() - new Date(a.created_date).getTime());

    const [blogs, setBlogs] = useState<BlogData[]>(sortedBlogData.slice(0, 6) || []);

    useEffect(() => {
        const fetchBlogs = async () => {
            const newBlogs = await fetchBlogData();
            setBlogs(newBlogs.slice(0, 6));
        };

        fetchBlogs();
    }, []);

    return (
        <section>
            <h2 className="text-gray-800 text-xl font-bold mb-4">Our Blog Posts</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="md:col-span-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {blogs?.map((post) => (
                            <article
                                key={post.blog_id}
                                className="rounded-lg p-2 bg-white relative shadow-md hover:shadow-lg transition-shadow pb-4"
                            >
                                <Link href={`/bond-academy/blog/${post.blog_id}`} className="group">
                                    <div className="relative w-full h-48 md:h-48">
                                        <Image
                                            src={post.blog_image ? getImageUrl(post.blog_image) : '/img/PlaceHolder.png'} // Adjust the path according to your image storage
                                            alt={post.blog_title}
                                            layout="fill"
                                            objectFit="cover"
                                            className="rounded-md"
                                            unoptimized
                                            // style={{ height: '200px', objectFit: 'cover' }}
                                        />
                                        <div className="absolute text-xs top-1 right-1 bg-[#ff7300] font-semibold  text-white px-2 py-1 rounded-br-lg rounded-lg">
                                            {post.category}
                                        </div>
                                    </div>

                                    <div className="flex flex-col px-2 items-start justify-between mt-4">
                                        <div className="text-xs text-gray-500 mb-2">{new Date(post.created_date).toLocaleDateString()}</div>
                                        <h3 className="text-md text-[#333333] font-semibold mb-2">{post.blog_title}</h3>
                                        <div
                                            className="text-gray-600 mb-4 text-sm line-clamp-3"
                                            dangerouslySetInnerHTML={{
                                                __html: post?.blog_description?.replace(/<[^>]*>/g, '').slice(0, 150) + '...',
                                            }}
                                        />
                                        <span className=" font-semibold text-blue-600 text-sm hover:text-blue-800">Read more →</span>
                                    </div>
                                </Link>
                            </article>
                        ))}
                    </div>
                    {sortedBlogData.length > 6 && (
                        <div className="flex ms-2 mt-6">
                            <Link
                                href="/bond-academy/blog"
                                className="px-4 py-2  text-blue-700 border border-blue-700 flex items-center rounded-md hover:bg-blue-100"
                            >
                                View More{' '}
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    xmlnsXlink="http://www.w3.org/1999/xlink"
                                    width="30px"
                                    height="20px"
                                    viewBox="-3 0 32 32"
                                    version="1.1"
                                >
                                    <g id="icomoon-ignore"></g>
                                    <path
                                        d="M13.11 29.113c7.243 0 13.113-5.871 13.113-13.113s-5.87-13.113-13.113-13.113c-7.242 0-13.113 5.871-13.113 13.113s5.871 13.113 13.113 13.113zM13.11 3.936c6.652 0 12.064 5.412 12.064 12.064s-5.412 12.064-12.064 12.064c-6.653 0-12.064-5.412-12.064-12.064s5.411-12.064 12.064-12.064z"
                                        fill="blue"
                                    ></path>
                                    <path
                                        d="M13.906 21.637l0.742 0.742 6.378-6.379-6.378-6.379-0.742 0.742 5.112 5.112h-12.727v1.049h12.727z"
                                        fill="blue"
                                    ></path>
                                </svg>
                            </Link>
                        </div>
                    )}
                </div>
                {/* Recent Posts sidebar */}
                <div className="md:col-span-1">
                    <h3 className="text-xl text-[#333333] font-semibold mb-2">Latest Posts</h3>
                    <ul className="space-y-4">
                        <div className="border-b grid grid-cols-1 md:grid-cols-1 gap-6">
                            {blogs?.map((post) => (
                                <article
                                    key={post.blog_id}
                                    className="flex items-center space-x-4 py-4 border-b border-gray-200 last:border-b-0"
                                >
                                    <div className="flex-shrink-0 w-24 h-24 relative">
                                        <Image
                                            src={post.blog_image ? getImageUrl(post.blog_image) : '/img/PlaceHolder.png'}
                                            alt={post.blog_title}
                                            layout="fill"
                                            objectFit="cover"
                                            className="rounded-md"
                                            unoptimized
                                        />
                                    </div>
                                    <div className="flex-grow min-w-0">
                                        <p className="text-xs text-gray-500 mb-1">{new Date(post.created_date).toLocaleDateString()}</p>
                                        <h3 className="text-sm text-[#333333] font-semibold mb-1 truncate">{post.blog_title}</h3>
                                        <div
                                            className="text-xs text-gray-600 line-clamp-2"
                                            dangerouslySetInnerHTML={{
                                                __html: post?.blog_description?.replace(/<[^>]*>/g, '').slice(0, 60) + '...',
                                            }}
                                        />
                                    </div>
                                </article>
                            ))}
                        </div>
                    </ul>
                </div>
            </div>
        </section>
    );
}
